import React, { useEffect, useMemo, useRef } from 'react';
import { parseReminderLine } from '../lib/reminderParser';
import { MANUAL_HIGHLIGHTS_ENABLED, parseHighlightMarker, parseHighlightSpans, cssVarForColor, isDoneLine } from '../lib/highlightMarkers';

/**
 * SyntaxBackdrop — Walter 2026-06-20. Always-on subtle highlighting of the
 * list's own structure, drawn BEHIND the (transparent-background) textarea.
 *
 * Same mirror-layer idea as the find overlay, but anchored inside the list's
 * container so it tracks the tile as it moves/scrolls without any
 * fixed-position measurement. It tints:
 *   - `#`/`##` header lines              → faint accent wash (whole line)
 *   - reminder lines: ONLY the date/time → faint amber wash (Walter 2026-06-20:
 *       "everything that functions as a reminder should be highlighted but …
 *       only highlight the time and date"). The temporal span is located with
 *       the app's own `parseReminderLine` (replaceStart/replaceEnd), so what
 *       lights up is exactly what listicity reads as the date/time.
 *
 * Log @dates are intentionally left UNMARKED (Walter 2026-06-20: "not sure we
 * need anything for logs — leave them unmarked").
 *
 * A line "functions as a reminder" when it starts with 🔔 / [DONE], OR the
 * whole list is a designated reminder list (`isReminderList`) — every line in
 * one is auto-treated as a reminder, so we parse each for a date/time span.
 *
 * Background washes only (no bold/size — those change letter widths and break
 * alignment). Intensities are tunable constants below. Scroll + width are
 * synced imperatively (no React re-render on scroll); segments recompute only
 * when the content changes.
 */

// Wash colors come from theme CSS variables so they adapt across all four
// palettes (warm dark/light + tech dark/light). Defined in index.css:
//   --syntax-header-tint   — beige wash on the whole header line
//   --syntax-reminder-tint — bell-gold wash on the reminder date/time span
// Log @dates are intentionally NOT marked (Walter opted to leave logs alone).
const TINT = {
  header:   'var(--syntax-header-tint)',
  reminder: 'var(--syntax-reminder-tint)',
  // "Mark as done" veil — Walter 2026-06-23. Unlike the washes (which sit
  // BEHIND the text), the done veil is rendered by a SEPARATE in-FRONT instance
  // (variant="veil", higher z-index) so this translucent layer greys the line's
  // text. Theme-aware: a white veil in light mode, a dark veil in dark mode.
  done:     'var(--syntax-done-veil)',
};

// Text-layout properties copied from the host textarea so wrapping is identical.
const COPY = [
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
  'letterSpacing', 'lineHeight', 'textTransform', 'wordSpacing', 'textIndent',
  'whiteSpace', 'wordBreak', 'overflowWrap', 'tabSize',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  // Walter 2026-08-21: iOS inflates text per-container and hides it from
  // getComputedStyle, so copying font-size is NOT enough to make the mirror
  // match the textarea. index.css pins this to 100% on both; copying it here
  // as well keeps the two in step if that ever changes.
  'webkitTextSizeAdjust',
];

const HEADER_RE = /^\s{0,3}#{1,6}\s/;
const BELL_RE = /^(?:🔔|\[DONE\]|🔕|~~🔔)/;

function computeSegments(content, isReminderList) {
  const segs = [];
  if (!content) return segs;
  const now = new Date();
  const lines = content.split('\n');
  let offset = 0;
  for (const line of lines) {
    const start = offset;
    const end = offset + line.length;
    const done = MANUAL_HIGHLIGHTS_ENABLED && isDoneLine(line);
    const hl = (!done && MANUAL_HIGHLIGHTS_ENABLED) ? parseHighlightMarker(line) : null;
    if (done) {
      // "Mark as done" → grey the WHOLE line. Rendered only by the in-front
      // veil instance (full-width band, like a header). No other wash applies.
      segs.push({ start, end, kind: 'done', block: true });
    } else if (hl) {
      // Manual priority highlight → wash the whole line in the chosen color.
      // Takes precedence over the auto header/reminder tints for that line.
      // Only the UNCLOSED form reaches here; a closed pair is a span (below).
      segs.push({ start, end, bg: cssVarForColor(hl.colorIndex) });
    } else if (MANUAL_HIGHLIGHTS_ENABLED && parseHighlightSpans(line).length > 0) {
      // Walter 2026-08-22: paired markers wash only the text between them —
      // `^important^ and the rest` colours one word. Mirrors the emphasis
      // markers. The washes are ranges, which this layer already draws for
      // reminder date/times, so a span needs no new machinery.
      for (const sp of parseHighlightSpans(line)) {
        segs.push({ start: start + sp.start, end: start + sp.end, bg: cssVarForColor(sp.colorIndex) });
      }
    } else if (HEADER_RE.test(line)) {
      // Headers wash the WHOLE LINE WIDTH (block:true → full-width band, so it
      // reads like a header bar — Walter 2026-06-23). `level` = number of #'s,
      // used to graduate the shade so H1/H2/H3 are distinguishable (2026-06-26).
      const hm = line.match(/^\s{0,3}(#{1,6})\s/);
      segs.push({ start, end, kind: 'header', block: true, level: hm ? hm[1].length : 1 });
    } else if (isReminderList || BELL_RE.test(line)) {
      // Functions as a reminder → tint the date/time block. Walter 2026-06-23:
      // highlight the WHOLE block (incl. "every"), not just the parser's core
      // span. Canonical bell lines are "🔔 <date/time>: <task>", so for a bell
      // line we wash from after the glyph up to the task colon (": " — a 9:30
      // time won't be mistaken for it). Otherwise fall back to the parser span,
      // extended left over a leading recurrence word the parser dropped.
      let handled = false;
      const bellM = line.match(BELL_RE);
      if (bellM) {
        let tStart = bellM[0].length;
        while (line[tStart] === ' ') tStart += 1;
        const colon = line.indexOf(': ', tStart);
        if (colon > tStart) {
          segs.push({ start: start + tStart, end: start + colon, kind: 'reminder' });
          handled = true;
        }
      }
      if (!handled) {
        const p = parseReminderLine(line, { allowQuestion: true, allowEmptyBody: true, now });
        if (p && p.isReminder && typeof p.replaceStart === 'number' && p.replaceStart >= 0
            && typeof p.replaceEnd === 'number' && p.replaceEnd > p.replaceStart) {
          // Parser offsets are in TRIMMED coordinates; shift by leading whitespace.
          const lead = line.length - line.trimStart().length;
          let s = lead + p.replaceStart;
          const e = lead + p.replaceEnd;
          const rec = line.slice(0, s).match(/\b(every(?:\s+other)?|each)\s+$/i);
          if (rec) s -= rec[0].length; // include "every "/"each "
          segs.push({ start: start + s, end: start + e, kind: 'reminder' });
        }
      }
    }
    // (Plain lines, incl. log @dates, are intentionally left unmarked.)
    offset = end + 1; // + the newline
  }
  return segs;
}

export default function SyntaxBackdrop({ content, textareaRef, isReminderList = false, enabled = true, variant = 'wash' }) {
  const innerRef = useRef(null);
  const isVeil = variant === 'veil';

  // Copy the host textarea's text-layout metrics so the mirror wraps text
  // IDENTICALLY (ListPanel and FocusedPanel use different padding/font-size),
  // then keep scroll position + content width synced. Width tracks clientWidth
  // because a vertical scrollbar narrows the textarea's text column.
  useEffect(() => {
    const ta = textareaRef && textareaRef.current;
    const inner = innerRef.current;
    if (!ta || !inner) return undefined;
    const cs = window.getComputedStyle(ta);
    COPY.forEach((k) => { inner.style[k] = cs[k]; });
    // Belt and braces: never let the mirror inflate independently of its host.
    inner.style.webkitTextSizeAdjust = '100%';
    const sync = () => {
      inner.style.transform = `translateY(${-ta.scrollTop}px)`;
      inner.style.width = `${ta.clientWidth}px`;
    };
    sync();
    ta.addEventListener('scroll', sync);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(sync); ro.observe(ta); }
    return () => { ta.removeEventListener('scroll', sync); if (ro) ro.disconnect(); };
  }, [textareaRef, content]);

  // The veil instance draws ONLY the done lines (in front of the text); the
  // wash instance draws everything else (behind the text). One computeSegments,
  // split by variant so a done line never gets both a wash and a veil.
  const segs = useMemo(() => {
    if (!enabled) return [];
    const all = computeSegments(content, isReminderList);
    return isVeil ? all.filter((s) => s.kind === 'done') : all.filter((s) => s.kind !== 'done');
  }, [content, enabled, isReminderList, isVeil]);

  const thisContainerStyle = isVeil ? veilContainerStyle : containerStyle;

  if (!enabled || segs.length === 0) {
    // Still render the (empty) container so the effect's refs stay stable.
    return (
      <div aria-hidden="true" style={thisContainerStyle}>
        <div ref={innerRef} style={mirrorStyle} />
      </div>
    );
  }

  // Build plain + marked spans across the whole content.
  segs.sort((a, b) => a.start - b.start || a.end - b.end);
  const parts = [];
  let cur = 0;
  segs.forEach((s, i) => {
    if (s.start < cur) return; // skip overlaps (e.g. a date inside a header line)
    if (s.start > cur) parts.push(<span key={`p${i}`}>{content.slice(cur, s.start)}</span>);
    // Header level → graduated shade (Walter 2026-06-26): the fade alone signals
    // depth (no bars/underlines). H1 = the tint compounded over itself (darkest),
    // H2 = the base tint, H3+ = the base tint faded. Theme-safe: it only scales
    // the single themed --syntax-header-tint, so it tracks all four palettes.
    let bg = s.bg || TINT[s.kind] || TINT.header;
    let levelOpacity;
    if (s.kind === 'header') {
      const lvl = s.level || 1;
      if (lvl === 1) bg = 'linear-gradient(var(--syntax-header-tint),var(--syntax-header-tint)),var(--syntax-header-tint)';
      else if (lvl >= 3) levelOpacity = 0.5;
    }
    parts.push(
      <mark
        key={`m${i}`}
        style={{
          background: bg,
          color: 'transparent', borderRadius: 2,
          ...(levelOpacity != null ? { opacity: levelOpacity } : null),
          // block:true (headers) → the wash spans the full line width so it
          // reads like a header bar. inline-block + width:100% keeps the text
          // left-aligned (alignment preserved) while the background fills right.
          ...(s.block ? { display: 'inline-block', width: '100%', borderRadius: 0 } : null),
        }}
      >
        {content.slice(s.start, s.end)}
      </mark>,
    );
    cur = s.end;
  });
  if (cur < content.length) parts.push(<span key="tail">{content.slice(cur)}</span>);

  return (
    <div aria-hidden="true" style={thisContainerStyle}>
      <div ref={innerRef} style={mirrorStyle}>{parts}</div>
    </div>
  );
}

const containerStyle = {
  position: 'absolute', inset: 0,
  overflow: 'hidden', pointerEvents: 'none',
  zIndex: 0,
};

// The done veil sits ABOVE the textarea (which is position:relative; zIndex:1)
// so its translucent rectangles dim the line's real text. pointer-events:none
// keeps clicks/typing flowing through to the textarea underneath.
const veilContainerStyle = {
  ...containerStyle,
  zIndex: 2,
};

// Base mirror box. Font / padding / wrapping + width are applied imperatively
// from the host textarea's computed style (see the effect above) so alignment
// holds for any editor. color transparent so only the mark backgrounds show;
// the real textarea text sits opaque on top.
const mirrorStyle = {
  position: 'absolute', top: 0, left: 0,
  boxSizing: 'border-box',
  margin: 0,
  whiteSpace: 'pre-wrap',
  color: 'transparent',
};
